import { CheckCircleFilled, PrinterOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Tag,
  Typography,
} from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import React, { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";
import moment from "moment";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import MultipleUpload from "../../../../../components/inputs/UploadMultipleFile";
import CredentialsCard from "../../../../../components/CredentialsCard";
import ApplicationFeeReceipt from "../../../../../components/ApplicationFeeReceipt";
import { serverUrl, cloudFrontUrl } from "../../../../../nestserver";
import BackButton from "../../../../../components/buttons/BackButton";
import { getEnrollmentYearOptions } from "../../../../../utils/Const";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";

const { Option } = Select;
const { Panel } = Collapse;

const GenderSelect = ({ value, onChange }) => {
  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder="Select Gender"
    >
      <Option value="male">Male</Option>
      <Option value="female">Female</Option>
      <Option value="others">Other</Option>
    </Select>
  );
};

const BloodGroupSelect = ({ value, onChange }) => {
  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder="Select Blood Group"
    >
      <Option value="A+">A+</Option>
      <Option value="B+">B+</Option>
      <Option value="O+">O+</Option>
      <Option value="A-">A-</Option>
      <Option value="B-">B-</Option>
      <Option value="O-">O-</Option>
      <Option value="AB+">AB+</Option>
      <Option value="AB-">AB-</Option>
    </Select>
  );
};

const AddEditApplication = ({
  formData,
  onFeeStatusChange,
  onStepChange,
  inquiry,
  onSubmitted,
}) => {
  const { id } = useParams();
  const [formFields, setFormFields] = useState([{}]);
  const [form] = Form.useForm();
  const { Title } = Typography;
  const [getResponse, setGetResponse] = useState();
  const schoolId = useSelector(selectSchoolId);
  const schoolQuery = useQuery(
    ["school", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => res.data),
    { enabled: !!schoolId }
  );
  const school = schoolQuery.data?.result?.data;
  const receiptRef = useRef();
  const [secondGuardianCheckbox, setSecondGuardianCheckbox] = useState(false);
  const [previousSchoolCheckbox, setPreviousSchoolCheckbox] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [routeId, setRouteId] = useState(null);
  const [transportationCheckbox, setTransportationCheckbox] = useState(false);
  const [accommodationCheckbox, setAccommodationCheckbox] = useState(false);
  const [daycareCheckbox, setDaycareCheckbox] = useState(false);
  // All sections start open so every field (including optional ones in
  // Sibling & Previous School / Transportation & Documents) is visible on
  // one page — collapse is available per-section if the admin wants it.
  const [activeSections, setActiveSections] = useState(["0", "1", "2", "3"]);
  const [formValues, setFormValues] = useState({});
  useEffect(() => {
    setFormValues(form.getFieldsValue(true));
  }, [form]);
  useEffect(() => {
    // AdmissionProfile's approve/reject gate only unlocks once the admin has
    // "reviewed all steps" — with everything on one page there's no longer a
    // step to advance through, so that's satisfied immediately on mount.
    onStepChange?.(3);
  }, [onStepChange]);

  // Sections made only of optional fields can never earn the check by
  // filling required fields — instead the admin marks them reviewed with an
  // explicit gesture: collapsing (minimizing) the section after looking at
  // it lights its green check.
  const [viewedSections, setViewedSections] = useState({});
  const handleSectionsChange = (keys) => {
    const collapsed = activeSections.filter((k) => !keys.includes(k));
    if (collapsed.length) {
      setViewedSections((prev) => {
        const next = { ...prev };
        collapsed.forEach((k) => {
          next[k] = true;
        });
        return next;
      });
    }
    setActiveSections(keys);
  };

  const [parentFileList, setParentFileList] = useState([]);

  const billingZones = useQuery(["transport-billing-zones"], async () => {
    const res = await axios.get(
      `${serverUrl}/admin/transport-billing-zone/list`,
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      }
    );
    return res.data?.result || [];
  });

  const selectedZone = useMemo(
    () => billingZones.data?.find((z) => z._id === selectedZoneId) || null,
    [billingZones.data, selectedZoneId]
  );

  const routes = useQuery(["school-routes"], async () => {
    const headers = { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` };
    const [v1Res, v2Res] = await Promise.all([
      axios.get(`${serverUrl}/routes`, { headers }),
      axios.get(`${serverUrl}/routes/v2`, { headers }),
    ]);
    const v1Data = v1Res.data?.result?.data || [];
    const v2Data = v2Res.data?.result?.data || [];
    return [
      ...v1Data.map((r) => ({ ...r, _version: "v1" })),
      ...v2Data.map((r) => ({ ...r, _version: "v2" })),
    ];
  });

  const routesDetail = useQuery(
    ["school-route-stops", routeId],
    async () => {
      const headers = { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` };
      const routeInList = (routes.data || []).find((r) => r._id === routeId);
      const url = routeInList?._version === "v2"
        ? `${serverUrl}/routes/v2/${routeId}`
        : `${serverUrl}/routes/${routeId}`;
      const res = await axios.get(url, { headers });
      return res.data?.result?.data?.route_map || [];
    },
    { enabled: !!routeId }
  );

  useEffect(() => {
    const response = { status: true };
    setGetResponse(response);
  }, []);
  const handleFileListChange = (fileList) => {
    setParentFileList(fileList);
  };

  const handleMarkFeeStatus = async (status) => {
    setFeeActionLoading(status);
    try {
      await axios.patch(
        `${serverUrl}/admin/application/${id}/fee`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      setApplicationFeeStatus(status);
      onFeeStatusChange?.(status);
      message.success(
        status === "paid" ? "Application fee marked as paid" : "Application fee waived"
      );
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Could not update the application fee"
      );
    } finally {
      setFeeActionLoading(null);
    }
  };

  const handleAddForm = () => {
    setFormFields([...formFields, {}]);
  };

  const handleRemoveForm = (index) => {
    const updatedFields = [...formFields];
    updatedFields.splice(index, 1);
    setFormFields(updatedFields);
  };
  const validateAlphabetsOnly = (_, value) => {
    const regex = /^[a-zA-Z\s]*$/;
    if (!regex.test(value)) {
      return Promise.reject("Please enter alphabets only");
    }
    return Promise.resolve();
  };

  const [referral, setReferral] = useState("");
  const [friendInfo, setFriendInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const handleReferralChange = (value) => {
    setReferral(value);
  };

  const handleFriendInfoChange = (field, value) => {
    setFriendInfo({ ...friendInfo, [field]: value });
  };
  const [uploadAdmissionPopup, setUploadAdmissionPopup] = useState(false);
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    relation: "",
    occupation: "",
    education: "",
    annualIncome: "",
    phone: "",
    email: "",
    aadhar: "",
    address: "",
    emergencyContact: "",
  });
  const [applicationNumber, setApplicationNumber] = useState();
  const [admissionNumber, setAdmissionNumber] = useState();
  const [applicationFeeAmount, setApplicationFeeAmount] = useState(0);
  const [applicationFeeStatus, setApplicationFeeStatus] = useState("not_required");

  const [feeActionLoading, setFeeActionLoading] = useState(null);
  const handlePrintReceipt = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `fee-receipt-${applicationNumber || ""}`,
  });
  const [disabilityStatus, setDisabilityStatus] = useState(false);
  function onChange(e) {
    setDisabilityStatus(e.target.value);
  }
  // Opened from an admission inquiry: pre-fill what the inquiry already knows.
  useEffect(() => {
    if (!inquiry || id) return;
    const prefill = {
      first_name: inquiry.first_name,
      last_name: inquiry.last_name,
      class: inquiry.class?._id || inquiry.class,
      dob: inquiry.dob ? moment(inquiry.dob, "YYYY-MM-DD") : undefined,
      father_name: inquiry.parent_name,
      gender: inquiry.gender,
      primary_contact_phone: inquiry.phone,
      primary_contact_email: inquiry.email,
      primary_contact_address: inquiry.address,
    };
    Object.keys(prefill).forEach((key) => {
      if (!prefill[key]) delete prefill[key];
    });
    form.setFieldsValue(prefill);
    setFormValues(form.getFieldsValue(true));
    // Gender and address are required on the application; inquiries saved
    // before those fields existed (or left blank) don't carry them.
    const missing = [];
    if (!inquiry.gender) missing.push("gender");
    if (!inquiry.address) missing.push("address");
    if (missing.length) {
      message.info(
        `This inquiry has no ${missing.join(" or ")} saved — please fill it in below.`
      );
    }
  }, [inquiry, id, form]);

  useEffect(() => {
    if (formData) {
      const response = formData;
      setGetResponse(response);
      setApplicationNumber(response?.application_number);
      setAdmissionNumber(response?.admission_id?.admission_number);
      setApplicationFeeAmount(response?.application_fee_amount || 0);
      setApplicationFeeStatus(response?.application_fee_status || "not_required");
      form.setFieldsValue({
        email: response.email,
        aadhar: response?.aadhar,
        phone: response?.phone,
        first_name: response?.first_name,
        last_name: response?.last_name,
        class: response?.class?._id,
        dob: moment(response.dob),
        gender: response?.gender,
        nationality: response?.nationality,
        mother_tongue: response?.mother_tongue,
        category: response?.category,
        enrollment_year: response?.enrollment_year,
        ethnicity: response?.ethnicity,
        birth_place: response?.birth_place,
        religion: response?.religion,
        blood_group: response?.health_details?.blood_group,
        school_name: response?.previous_school?.name,
        graduated_year: response?.previous_school?.graduated_year,
        grade: response?.previous_school?.grade,
        class_completed: response?.previous_school?.class_completed,
        reference: response?.previous_school?.phone,
        change_reason: response?.previous_school?.change_reason,
        mother_name: response?.mother_name,
        mother_phone: response?.mother_phone,
        mother_email: response?.mother_email,
        mother_aadhar: response?.mother_aadhar,
        mother_education: response?.mother_education,
        mother_occupation: response?.mother_occupation,
        mother_annual_income: response?.mother_annual_income,
        father_name: [
          response?.primary_contact?.first_name,
          response?.primary_contact?.last_name,
        ]
          .filter(Boolean)
          .join(" "),
        primary_contact_education: response?.primary_contact?.education,
        primary_contact_occupation: response?.primary_contact?.occupation,
        primary_contact_annual_income: response?.primary_contact?.annual_income,
        primary_contact_phone: response?.primary_contact?.contact,
        primary_contact_email: response?.primary_contact?.email,
        primary_contact_aadhar: response?.primary_contact?.aadhar,
        primary_contact_address: response?.primary_contact?.address,
        primary_emergency_contact: response?.primary_contact?.emergency_contact,
        secondary_contact_first_name: response?.secondary_contact?.first_name,
        secondary_contact_last_name: response?.secondary_contact?.last_name,
        secondary_contact_relation: response?.secondary_contact?.relation,
        secondary_contact_education: response?.secondary_contact?.education,
        secondary_contact_annual_income:
          response?.secondary_contact?.annual_income,
        secondary_contact_occupation: response?.secondary_contact?.occupation,
        secondary_contact_phone: response?.secondary_contact?.contact,
        secondary_contact_email: response?.secondary_contact?.email,
        secondary_contact_aadhar: response?.secondary_contact?.aadhar,
        secondary_emergency_contact:
          response?.secondary_contact?.emergency_contact,
        secondary_contact_address: response?.secondary_contact?.address,
        is_transportation_required: response?.is_transportation_required,
        transport_zone_id: response?.transport_zone_id?._id || response?.transport_zone_id || null,
        transport_destination_id: response?.transport_destination_id || null,
        route_id: response?.route_id?._id || response?.route_id || null,
        route_entry_point_id: response?.route_entry_point_id?._id || response?.route_entry_point_id || null,
        is_accommodation_required: response?.is_accommodation_required,
        is_daycare_enabled: response?.is_daycare_enabled,
        is_existing_student: response?.is_existing_student === true,
      });
      setSelectedZoneId(response?.transport_zone_id?._id || response?.transport_zone_id || null);
      setRouteId(response?.route_id?._id || response?.route_id || null);
      setTransportationCheckbox(response?.is_transportation_required);
      setAccommodationCheckbox(response?.is_accommodation_required);
      setDaycareCheckbox(response?.is_daycare_enabled);
      setSecondGuardianCheckbox(
        Boolean(
          response?.secondary_contact?.first_name ||
            response?.secondary_contact?.last_name
        )
      );
      setPreviousSchoolCheckbox(Boolean(response?.previous_school?.name));
      setDisabilityStatus(response?.health_details?.is_disabled);
      response?.existing_siblings?.forEach((sibling, index) => {
        form.setFieldsValue({
          [`name${index + 1}`]: sibling.name,
          [`student_id${index + 1}`]: sibling.student_id,
        });
      });
      if (response?.documents && response?.documents.length > 0) {
        setParentFileList(response?.documents);
      }
      setFormFields(response?.existing_siblings);
      if (response?.referral?.is_friend) {
        form.setFieldsValue({
          referral: response?.referral?.is_friend ? "Friend" : "",
          friendName: response?.referral?.name,
          friendPhone: response?.referral?.phone,
          friendEmail: response?.referral?.email,
        });
        setReferral("friend");
        setFriendInfo({
          name: response?.referral?.name,
          phone: response?.referral?.phone,
          email: response?.referral?.email,
        });
      } else
        form.setFieldsValue({
          referral: response?.referral?.name,
        });
      setFormValues(form.getFieldsValue(true));
    }
  }, [formData, form]);

  const onFinish = () => {
    // Steps are conditionally rendered (only the current one is mounted),
    // so the `values` antd's Form would normally pass here only reflects
    // whichever step is on screen at submit time — earlier steps' fields
    // (first_name, dob, gender, class, etc.) silently drop out even though
    // the form is still holding onto them. getFieldsValue(true) reads the
    // full store regardless of what's currently mounted.
    const values = form.getFieldsValue(true);
    const existingSiblings = [];
    formFields.forEach((field, index) => {
      const nameValue = values[`name${index + 1}`] || "";
      const studentIdValue = values[`student_id${index + 1}`] || "";
      if (nameValue.trim() || studentIdValue.trim()) {
        existingSiblings.push({ name: nameValue, student_id: studentIdValue });
      }
    });

    // Primary Contact is always the father — a single "Father's Name" field
    // reads cleanest, but Contact's schema (shared with Secondary Contact)
    // still stores first/last name separately, so split it back on submit.
    const [fatherFirstName, ...fatherLastNameParts] = (
      values.father_name || ""
    )
      .trim()
      .split(/\s+/);
    const fatherLastName = fatherLastNameParts.join(" ");

    // "Reference Phone/Email" is one input for either a phone or an email,
    // but previous_school has separate phone/email fields in the schema —
    // route the value to whichever one it actually matches instead of
    // stamping it into both.
    const referenceValue = (values.reference || "").trim();
    const referenceIsEmail = /^\S+@\S+\.\S+$/.test(referenceValue);
    const referenceIsPhone = /^(\+\d{1,3}[- ]?)?\d{10}$/.test(referenceValue);

    const dataToSend = {
      first_name: values.first_name,
      last_name: values.last_name,
      dob: values.dob ? moment(values.dob).format("YYYY-MM-DD") : values.dob,
      gender: values.gender,
      nationality: values.nationality,
      aadhar: values.aadhar,
      email: values.email,
      phone: values.phone,
      birth_place: values.birth_place,
      religion: values.religion,
      ethnicity: values.ethnicity,
      class: values?.class,
      enrollment_year: values.enrollment_year,
      mother_tongue: values.mother_tongue,
      mother_name: values.mother_name,
      mother_phone: values.mother_phone,
      mother_email: values.mother_email,
      mother_aadhar: values.mother_aadhar,
      mother_education: values.mother_education,
      mother_occupation: values.mother_occupation,
      mother_annual_income: values.mother_annual_income,
      referral: {},
      category: values.category,
      documents: parentFileList,
      primary_contact: {
        first_name: fatherFirstName || "",
        last_name: fatherLastName,
        relation: "Father",
        occupation: values.primary_contact_occupation,
        education: values.primary_contact_education,
        annual_income: values.primary_contact_annual_income,
        contact: values.primary_contact_phone,
        email: values.primary_contact_email,
        aadhar: values.primary_contact_aadhar,
        address: values.primary_contact_address,
        emergency_contact: values.primary_emergency_contact,
      },
      ...(secondGuardianCheckbox && {
        secondary_contact: {
          first_name: values.secondary_contact_first_name,
          last_name: values.secondary_contact_last_name,
          relation: values.secondary_contact_relation,
          occupation: values.secondary_contact_occupation,
          education: values.secondary_contact_education,
          annual_income: values.secondary_contact_annual_income,
          contact: values.secondary_contact_phone,
          email: values.secondary_contact_email,
          aadhar: values.secondary_contact_aadhar,
          address: values.secondary_contact_address,
          emergency_contact: values.secondary_emergency_contact,
        },
      }),
      existing_siblings: existingSiblings,
      health_details: {
        blood_group: values.blood_group,
        is_disabled: disabilityStatus,
      },
      ...(previousSchoolCheckbox && {
        previous_school: {
          name: values.school_name,
          grade: values.grade,
          graduated_year: values.graduated_year,
          phone: referenceIsPhone ? referenceValue : "",
          email: referenceIsEmail ? referenceValue : "",
          class_completed: values.class_completed,
          change_reason: values.change_reason,
        },
      }),
      is_transportation_required: transportationCheckbox,
      transport_zone_id: values.transport_zone_id || null,
      transport_destination_id: values?.transport_destination_id || null,
      route_id: values?.route_id || null,
      route_entry_point_id: values?.route_entry_point_id || null,
      is_accommodation_required: accommodationCheckbox,
      is_daycare_enabled: daycareCheckbox,
      ...(!id && {
        is_existing_student: values.is_existing_student === true,
      }),
      ...(!id && inquiry?._id && { inquiry_id: inquiry._id }),
    };
    if (referral === "friend") {
      dataToSend.referral = {
        is_friend: true,
        name: friendInfo.name,
        phone: friendInfo.phone,
        email: friendInfo.email,
      };
    } else {
      dataToSend.referral = {
        is_friend: false,
        name: values.referral,
      };
    }

    if (id) {
      axios
        .put(serverUrl + "/admin/application/" + id, dataToSend, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => {
          setParentFileList(null);
          if (!res?.data?.success) {
            message.error(res?.data?.message);
          } else if (res?.data?.success) {
            message.success(res?.data?.message);
          }
        });
    } else
      axios
        .post(serverUrl + "/admin/application/", dataToSend, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => {
          if (res?.data?.success === false) {
            // e.g. duplicate applicant, or existing student without the
            // fee marked Paid/Waived — nothing was created, keep the form.
            message.error(res?.data?.message);
            return;
          }
          form.resetFields();
          setParentFileList(null);
          onSubmitted?.();
          const registration = res?.data?.registration;
          if (registration?.student?.username) {
            // Existing student: approved + registered in one go — this is
            // the only moment the generated credentials are on screen.
            Modal.success({
              title: "Existing student registered",
              width: 480,
              content: (
                <CredentialsCard
                  note={res?.data?.message}
                  student={registration.student}
                  parent={registration.parent}
                />
              ),
            });
          } else if (res?.data?.auto_approve_failed) {
            Modal.warning({
              title: "Application created, registration pending",
              content: res?.data?.message,
            });
          } else {
            message.success(res?.data?.message);
          }
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            message.error(err?.response?.data?.message);
          }
        });
  };

  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);
  //transporatation add

  const updateDataForm = (paramName, value) => {
    setData({ ...data, [paramName]: value });
  };

  // Ordered to match how the sections are laid out below (Student & Health,
  // then Sibling & Previous School, then Parents & Contact, then
  // Transportation) rather than the wizard's old step order.
  const stepTitles = [
    "Student & Health",
    "Parents & Contact",
    "Sibling & Previous School",
    "Transportation & Documents",
  ];

  // Only fields that are actually `required` in the form below count toward
  // a section's green check — Sibling/Previous School and Transportation
  // have no required fields, so those two are always considered complete.
  const REQUIRED_FIELDS_BY_SECTION = [
    ["first_name", "last_name", "gender", "dob", "class", "nationality"],
    [
      "father_name",
      "primary_contact_phone",
      "primary_contact_address",
    ],
    [],
    [],
  ];

  const isFieldFilled = (value) => {
    if (value === undefined || value === null) return false;
    if (moment.isMoment(value)) return value.isValid();
    if (typeof value === "string") return value.trim() !== "";
    return true;
  };

  // Sections with required fields show the check once all of them are
  // filled. Sections with no required fields (Sibling/Previous School,
  // Transportation) show it once the admin has scrolled through them —
  // reviewed counts as complete there, keeping the headers consistent.
  const isSectionComplete = (index) => {
    const fields = REQUIRED_FIELDS_BY_SECTION[index];
    if (fields.length === 0) return viewedSections[index] === true;
    return fields.every((field) => isFieldFilled(formValues[field]));
  };

  // Every field that lives in each section (not just the required ones) —
  // used only to figure out which collapsed panel to reopen when Submit
  // fails validation on a field the admin never scrolled to.
  const FIELDS_BY_SECTION = [
    [
      "is_existing_student",
      "first_name",
      "last_name",
      "dob",
      "gender",
      "nationality",
      "aadhar",
      "class",
      "enrollment_year",
      "mother_tongue",
      "email",
      "phone",
      "birth_place",
      "category",
      "religion",
      "ethnicity",
      "blood_group",
    ],
    [
      "father_name",
      "primary_contact_phone",
      "primary_contact_email",
      "primary_contact_aadhar",
      "primary_contact_education",
      "primary_contact_occupation",
      "primary_contact_annual_income",
      "mother_name",
      "mother_phone",
      "mother_email",
      "mother_aadhar",
      "mother_education",
      "mother_occupation",
      "mother_annual_income",
      "primary_contact_address",
      "primary_emergency_contact",
      "secondary_contact_first_name",
      "secondary_contact_last_name",
      "secondary_contact_relation",
      "secondary_contact_occupation",
      "secondary_contact_education",
      "secondary_contact_annual_income",
      "secondary_contact_phone",
      "secondary_contact_email",
      "secondary_contact_aadhar",
      "secondary_contact_address",
      "secondary_emergency_contact",
    ],
    [
      ...formFields.map((_, index) => `name${index + 1}`),
      ...formFields.map((_, index) => `student_id${index + 1}`),
      "school_name",
      "class_completed",
      "grade",
      "graduated_year",
      "reference",
      "change_reason",
    ],
    [
      "is_transportation_required",
      "transport_zone_id",
      "transport_destination_id",
      "is_accommodation_required",
      "referral",
      "friendName",
      "friendEmail",
      "friendPhone",
    ],
  ];

  // Collapsed panels only mount their fields once opened, so without
  // `forceRender` on every Panel a required field the admin never scrolled
  // to would never even be validated — this is the fallback for the rest:
  // if something still fails, pop open whichever section(s) it lives in so
  // the error is actually visible instead of failing silently.
  const onFinishFailed = ({ errorFields }) => {
    setActiveSections((prev) => {
      const next = new Set(prev);
      errorFields.forEach(({ name }) => {
        const fieldName = name[0];
        const sectionIndex = FIELDS_BY_SECTION.findIndex((fields) =>
          fields.includes(fieldName)
        );
        if (sectionIndex !== -1) next.add(String(sectionIndex));
      });
      return Array.from(next);
    });
    message.error("Please fill all required fields — check the sections opened below.");
  };

  return (
    <div>
      <Form
        form={form}
        name="dynamic_rule"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={(_, allValues) => setFormValues(allValues)}
        autoComplete="off"
        id="applicationForm"
        className="admission-compact-form"
      >
        <div className="form-header">
          <h3 className="">Form</h3>
          <div className="flex justify-between items-center">
            <div className="flex items-center" style={{ gap: "0.5rem" }}>
              {school?.logo && (
                <img
                  className="rounded-full object-cover"
                  style={{ width: "2rem", height: "2rem" }}
                  src={`${cloudFrontUrl}/common/${school.logo}`}
                  alt="School logo"
                />
              )}
              {school?.name && (
                <span className="text-sm font-semibold capitalize">
                  {school.name}
                </span>
              )}
            </div>
            <div>
              <div className="p-2 text-right">
                <BackButton />
              </div>
              {applicationNumber && (
                <h4>
                  Application Number: <span>{applicationNumber}</span>
                </h4>
              )}
              {admissionNumber && (
                <h4>
                  Admission Number: <span>{admissionNumber}</span>
                </h4>
              )}
              {/* Existing students never owe an application fee */}
              {!!id && formData?.is_existing_student && (
                <div
                  className="pt-2 flex items-center justify-end"
                  style={{ gap: "0.5rem" }}
                >
                  <span className="text-sm">Application Fee:</span>
                  <Tag>N/A</Tag>
                </div>
              )}
              {!!id && applicationFeeStatus !== "not_required" && (
                <div className="pt-2 flex items-center justify-end" style={{ gap: "0.5rem" }}>
                  <span className="text-sm">
                    Application Fee: ₹{applicationFeeAmount}
                  </span>
                  <Tag
                    color={
                      applicationFeeStatus === "paid"
                        ? "green"
                        : applicationFeeStatus === "waived"
                        ? "blue"
                        : "orange"
                    }
                  >
                    {applicationFeeStatus === "paid"
                      ? "Paid"
                      : applicationFeeStatus === "waived"
                      ? "Waived"
                      : "Pending"}
                  </Tag>
                  {applicationFeeStatus === "pending" && (
                    <>
                      <Button
                        size="small"
                        type="primary"
                        loading={feeActionLoading === "paid"}
                        disabled={feeActionLoading === "waived"}
                        onClick={() => handleMarkFeeStatus("paid")}
                      >
                        Mark Paid
                      </Button>
                      <Button
                        size="small"
                        loading={feeActionLoading === "waived"}
                        disabled={feeActionLoading === "paid"}
                        onClick={() => handleMarkFeeStatus("waived")}
                      >
                        Waive
                      </Button>
                    </>
                  )}
                  {applicationFeeStatus === "paid" && (
                    <Button
                      size="small"
                      icon={
                        <PrinterOutlined
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      }
                      onClick={handlePrintReceipt}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Print Receipt
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {applicationFeeStatus === "paid" && (
          <div style={{ display: "none" }}>
            <ApplicationFeeReceipt
              ref={receiptRef}
              school={school}
              applicationNumber={applicationNumber}
              studentName={[
                form.getFieldValue("first_name"),
                form.getFieldValue("last_name"),
              ]
                .filter(Boolean)
                .join(" ")}
              amount={applicationFeeAmount}
              paymentDate={formData?.application_fee_recorded_at}
            />
          </div>
        )}

        <div className="pb-8">
          <p className="text-xs text-gray-500 pb-2">
            Click a section's header to collapse or expand it
          </p>
          <Collapse
            ghost
            activeKey={activeSections}
            onChange={handleSectionsChange}
            className="admission-collapse"
          >
            <Panel
              key="0"
              forceRender
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    paddingRight: "0.5rem",
                  }}
                >
                  <span>{stepTitles[0]}</span>
                  <CheckCircleFilled
                    style={{
                      color: isSectionComplete(0) ? "#16a34a" : "#d1d5db",
                      fontSize: "1.35rem",
                    }}
                  />
                </div>
              }
            >
            <div>
              <Title level={2}>Student Details</Title>

              {/* Existing (already-enrolled) students skip the pending
                  queue: the application is approved + registered the moment
                  it is submitted. Locked after creation. */}
              <div className="admission-form ">
                <Form.Item
                  name="is_existing_student"
                  label="Student Type"
                  className="w-30"
                  initialValue={false}
                >
                  <Radio.Group className="admission-radio-group" disabled={!!id}>
                    <Radio value={false}>New</Radio>
                    <Radio value={true}>Existing</Radio>
                  </Radio.Group>
                </Form.Item>
                {!id &&
                  formValues.is_existing_student === true &&
                  (school?.application_fee || 0) > 0 && (
                    <Form.Item
                      label="Application Fee"
                      className="w-30"
                      extra="Not required for existing students."
                    >
                      <Tag>N/A</Tag>
                    </Form.Item>
                  )}
              </div>

              <div className="admission-form ">
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[
                    {
                      required: true,
                      message: "First Name is required",
                    },
                    {
                      message:
                        "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                      validator: (_, value) => {
                        if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                          );
                        }
                      },
                    },
                  ]}
                  className="w-30"
                >
                  <Input placeholder="Please input your name" />
                </Form.Item>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[
                    {
                      required: true,
                      message: "Last Name is required",
                    },
                    {
                      message:
                        "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                      validator: (_, value) => {
                        if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                          );
                        }
                      },
                    },
                  ]}
                  className="w-30"
                >
                  <Input placeholder="Please input your name" />
                </Form.Item>
                <Form.Item
                  name="gender"
                  label="Gender"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Gender is required",
                    },
                  ]}
                >
                  <GenderSelect />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="dob"
                  label="Date Of Birth"
                  rules={[
                    {
                      required: true,
                      message: "Date of Birth  is required",
                    },
                  ]}
                  className="w-30"
                >
                  {/* No getPopupContainer here on purpose: anchoring the
                      calendar to the flex row misplaced it OVER the input,
                      hiding what the admin types. Rendered to the body it
                      sits below the field, so the date can be typed
                      (DD-MM-YYYY + Enter) with the calendar as a fallback. */}
                  <DatePicker
                    format="DD-MM-YYYY"
                    placeholder="DD-MM-YYYY"
                    disabledDate={(current) =>
                      current && current > moment().endOf("day")
                    }
                  />
                </Form.Item>
                <Form.Item name="birth_place" label="Birth Place" className="w-30">
                  <Input />
                </Form.Item>
                <Form.Item
                  name="aadhar"
                  label="Aadhar"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{12}$/, // Updated regex pattern to match 12 digits
                      message: "Please enter a valid 12-digit Number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 12)}
                >
                  <Input maxLength={12} type="number" min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
              </div>
              <div className="admission-form">
                <Form.Item
                  name="enrollment_year"
                  label="Enroll Year"
                  className="w-30"
                  initialValue={getEnrollmentYearOptions()[1].value}
                >
                  <Select>
                    {getEnrollmentYearOptions().map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Class"
                  name="class"
                  className="w-full"
                  rules={[
                    {
                      required: true,
                      message: "Please select a class",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    showArrow
                    placeholder="Select Class"
                    className="w-full bg-gray-300"
                    style={{
                      border: "6px",
                      backgroundColor: "#EBEBEB",
                    }}
                  >
                    {classes.isSuccess &&
                      classes?.data?.map((data) => {
                        return (
                          <Option key={data._id} value={data._id}>
                            {data.name}
                          </Option>
                        );
                      })}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="nationality"
                  label="Nationality"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Nationality is required",
                    },
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </div>

              <div className="admission-form ">
                <Form.Item
                  name="mother_tongue"
                  label="Mother Tongue"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="religion"
                  label="Religion"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="category"
                  label="Category"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="ethnicity"
                  label="Ethnicity"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email",
                    },
                  ]}
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Phone"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/, // Adjust the regex pattern as needed
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input type="number" maxLength={10} min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
              </div>
            </div>
            <div>
              <Title level={2}>Health Details</Title>
              <div className="admission-form ">
                <Form.Item name="blood_group" label="Blood Group" className="w-30">
                  <BloodGroupSelect />
                </Form.Item>
                <div className="group-radio flex">
                  <h5 className="text-sm font-normal">Disabled:</h5>
                  <Radio.Group
                    className="admission-radio-group"
                    onChange={onChange}
                    value={disabilityStatus}
                    name="is_disabled"
                  >
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </div>
              </div>
            </div>
            </Panel>
            <Panel
              key="1"
              forceRender
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    paddingRight: "0.5rem",
                  }}
                >
                  <span>{stepTitles[1]}</span>
                  <CheckCircleFilled
                    style={{
                      color: isSectionComplete(1) ? "#16a34a" : "#d1d5db",
                      fontSize: "1.35rem",
                    }}
                  />
                </div>
              }
            >
            <div>
              <Title level={2}>Parents & Contact</Title>
              <Title level={4} className="admission-subheading">
                Father's Details
              </Title>
              <div className="admission-form ">
                <Form.Item
                  name="father_name"
                  label="Name"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Father's name is required",
                    },
                    {
                      message:
                        "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                      validator: (_, value) => {
                        if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="primary_contact_phone"
                  label="Phone"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Phone is required",
                    },
                    {
                      pattern: /^[0-9]{10}$/, // Adjust the regex pattern as needed
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("phone", e.target.value);
                    }}
                    type="number"
                    maxLength={10}
                    min={0}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
                <Form.Item
                  name="primary_contact_email"
                  label="Email"
                  className="w-30"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("email", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="primary_contact_aadhar"
                  label="Aadhaar"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{12}$/, // Updated regex pattern to match 12 digits
                      message: "Please enter a valid 12-digit Number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 12)}
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("aadhar", e.target.value);
                    }}
                    maxLength={12}
                    type="number"
                    min={0}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="primary_contact_education"
                  label="Education"
                  className="w-30"
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("education", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="primary_contact_occupation"
                  label="Occupation"
                  className="w-30"
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("occupation", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="primary_contact_annual_income"
                  label="Annual Income"
                  className="w-30"
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("annualIncome", e.target.value);
                    }}
                  />
                </Form.Item>
              </div>

              <Title level={4} className="admission-subheading">
                Mother's Details
              </Title>
              <div className="admission-form ">
                <Form.Item
                  name="mother_name"
                  label="Name"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="mother_phone"
                  label="Phone"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input
                    type="number"
                    maxLength={10}
                    min={0}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
                <Form.Item
                  name="mother_email"
                  label="Email"
                  className="w-30"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="mother_aadhar"
                  label="Aadhaar"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{12}$/,
                      message: "Please enter a valid 12-digit Number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 12)}
                >
                  <Input maxLength={12} type="number" min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="mother_education"
                  label="Education"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="mother_occupation"
                  label="Occupation"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="mother_annual_income"
                  label="Annual Income"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
              </div>

              <Title level={4} className="admission-subheading">
                Address &amp; Emergency Contact
              </Title>
              <div className="admission-form ">
                <Form.Item
                  name="primary_contact_address"
                  label="Address"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Address is required",
                    },
                  ]}
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("address", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="primary_emergency_contact"
                  label="Emergency Contact"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/, // Adjust the regex pattern as needed
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input
                    onChange={(e) => {
                      updateDataForm("emergencyContact", e.target.value);
                    }}
                    type="number"
                    maxLength={10}
                    min={0}
                    onWheel={(e) => e.target.blur()}
                  />
                </Form.Item>
              </div>
            </div>
            <div>
              <Title level={2}>Secondary Contact</Title>
              <Form.Item name="hasSecondGuardian" valuePropName="checked">
                <Checkbox
                  onChange={(e) => setSecondGuardianCheckbox(e.target.checked)}
                  checked={secondGuardianCheckbox}
                >
                  Add Guardian?
                </Checkbox>
              </Form.Item>
              {secondGuardianCheckbox && (
                <>
              <div className="admission-form ">
                <Form.Item
                  name="secondary_contact_first_name"
                  label="First Name"
                  className="w-30"
                  rules={[
                    {
                      message:
                        "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                      validator: (_, value) => {
                        if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_last_name"
                  label="Last Name"
                  className="w-30"
                  rules={[
                    {
                      message:
                        "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                      validator: (_, value) => {
                        if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_relation"
                  label="Relation"
                  className="w-30"
                  rules={[
                    {
                      validator: validateAlphabetsOnly,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="secondary_contact_occupation"
                  label="Occupation"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_education"
                  label="Education"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_annual_income"
                  label="Annual Income"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="secondary_contact_phone"
                  label="Phone"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/, // Adjust the regex pattern as needed
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input type="number" maxLength={10} min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_email"
                  label="Email"
                  className="w-30"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_contact_aadhar"
                  label="Aadhar"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{12}$/, // Updated regex pattern to match 12 digits
                      message: "Please enter a valid 12-digit Number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 12)}
                >
                  <Input maxLength={12} type="number" min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
              </div>
              <div className="admission-form ">
                <Form.Item
                  name="secondary_contact_address"
                  label="Address"
                  className="w-30"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="secondary_emergency_contact"
                  label="Emergency Contact"
                  className="w-30"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/, // Adjust the regex pattern as needed
                      message: "Please enter a valid 10-digit phone number!",
                    },
                  ]}
                  getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                >
                  <Input type="number" maxLength={10} min={0} onWheel={(e) => e.target.blur()} />
                </Form.Item>
              </div>
              </>
              )}
            </div>
            </Panel>
            <Panel
              key="2"
              forceRender
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    paddingRight: "0.5rem",
                  }}
                >
                  <span>{stepTitles[2]}</span>
                  <CheckCircleFilled
                    style={{
                      color: isSectionComplete(2) ? "#16a34a" : "#d1d5db",
                      fontSize: "1.35rem",
                    }}
                  />
                </div>
              }
            >
            <div>
              <Title level={2}>Existing Sibling</Title>
              <div className="pb-3">
                {formFields.map((field, index) => (
                  <div
                    key={index}
                    className={`admission-form ${index === 0 ? "w-92" : ""}`}
                  >
                    <Form.Item
                      name={`name${index + 1}`}
                      label="Name"
                      className="w-92"
                      rules={[
                        {
                          message:
                            "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed.",
                          validator: (_, value) => {
                            if (/^[a-zA-Z][a-zA-Z\s]*$/.test(value)) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                "Name must start with a letter and can only contain letters. Numbers and symbols are not allowed."
                              );
                            }
                          },
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={`student_id${index + 1}`}
                      label="Student Id"
                      className="w-30"
                    >
                      <Input />
                    </Form.Item>
                    {index > 0 && (
                      <Button danger onClick={() => handleRemoveForm(index)}>
                        {" "}
                        Remove{" "}
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="primary" onClick={handleAddForm}>
                  {" "}
                  Add{" "}
                </Button>
              </div>
            </div>
            <div>
              <Title level={2}>Previous School</Title>
              <Form.Item name="hasPreviousSchool" valuePropName="checked">
                <Checkbox
                  onChange={(e) => setPreviousSchoolCheckbox(e.target.checked)}
                  checked={previousSchoolCheckbox}
                >
                  Was the student previously enrolled at another school?
                </Checkbox>
              </Form.Item>
              {previousSchoolCheckbox && (
                <>
                  <div className="admission-form ">
                    <Form.Item name="school_name" label="School Name" className="w-30">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="class_completed"
                      label="Class Completed"
                      className="w-30"
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="grade" label="Overall Grade" className="w-30">
                      <Input />
                    </Form.Item>
                  </div>
                  <div className="admission-form ">
                    <Form.Item
                      name="graduated_year"
                      label="Year Completed"
                      className="w-30"
                      rules={[
                        {
                          pattern: /^\d{4}$/, // Ensures exactly 4 digits
                          message: "Enroll year must be a 4-digit number!",
                        },
                      ]}
                      getValueFromEvent={(e) => e.target.value.slice(0, 4)}
                    >
                      <Input type="number" maxLength={4} min={0} onWheel={(e) => e.target.blur()} />
                    </Form.Item>
                    <Form.Item
                      name="reference"
                      label="Reference Phone/Email"
                      className="w-30"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (
                              value &&
                              !/^(\+\d{1,3}[- ]?)?\d{10}$/.test(value) &&
                              !/^\S+@\S+\.\S+$/.test(value)
                            ) {
                              return Promise.reject(
                                "Please enter a valid phone number or email"
                              );
                            } else {
                              return Promise.resolve();
                            }
                          },
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                  <div>
                    <div className="text-base font-normal pb-2">Reason For Change:</div>
                    <Form.Item name="change_reason">
                      <Input.TextArea
                        rows={3}
                        placeholder="Reason"
                        className="w-full border focus:outline-none resize-none"
                      />
                    </Form.Item>
                  </div>
                </>
              )}
            </div>
            </Panel>
            <Panel
              key="3"
              forceRender
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    paddingRight: "0.5rem",
                  }}
                >
                  <span>{stepTitles[3]}</span>
                  <CheckCircleFilled
                    style={{
                      color: isSectionComplete(3) ? "#16a34a" : "#d1d5db",
                      fontSize: "1.35rem",
                    }}
                  />
                </div>
              }
            >
          <div>
            <Title level={2}>Transportation</Title>
            <Form.Item name="is_transportation_required" valuePropName="checked">
              <Checkbox
                onChange={(e) => {
                  setTransportationCheckbox(e.target.checked);
                  form.resetFields(["transport_zone_id", "transport_destination_id", "route_id", "route_entry_point_id"]);
                  setSelectedZoneId(null);
                  setRouteId(null);
                  if (e.target.checked) {
                    setAccommodationCheckbox(false);
                    form.setFieldsValue({ is_accommodation_required: false });
                  }
                }}
                checked={transportationCheckbox}
              >
                Transportation require?
              </Checkbox>
            </Form.Item>
            {transportationCheckbox && (
              <div className="admission-form">
                <Form.Item
                  label="Transport Zone"
                  name="transport_zone_id"
                  className="w-full"
                >
                  <Select
                    showSearch
                    showArrow
                    placeholder="Select Zone"
                    className="bg-gray-300"
                    style={{ border: "6px", backgroundColor: "#EBEBEB" }}
                    onChange={(value) => {
                      setSelectedZoneId(value);
                      form.resetFields(["transport_destination_id"]);
                    }}
                  >
                    {billingZones?.isSuccess &&
                      billingZones?.data?.map((zone) => (
                        <Option key={zone._id} value={zone._id}>
                          {zone.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Destination"
                  name="transport_destination_id"
                  className="w-full"
                >
                  <Select
                    showSearch
                    showArrow
                    placeholder="Select Destination"
                    className="w-full bg-gray-300"
                    style={{ border: "6px", backgroundColor: "#EBEBEB" }}
                    disabled={!selectedZoneId}
                  >
                    {selectedZone?.destinations?.map((dest) => (
                      <Option key={dest._id} value={dest._id}>
                        {dest.name} — ₹{Number(dest.amount).toLocaleString("en-IN")}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            )}
            {transportationCheckbox && (
              <div className="mt-4" style={{ borderTop: "1px solid #e8e8e8", paddingTop: 16 }}>
                <div className="font-medium text-gray-600 mb-3" style={{ fontSize: 13 }}>Bus Tracking (Live Tracking App)</div>
                <div className="grid grid-cols-2 gap-x-4">
                  <Form.Item label="Route" name="route_id" className="w-full">
                    <Select
                      showSearch
                      showArrow
                      allowClear
                      placeholder="Select Route"
                      className="w-full"
                      loading={routes.isLoading}
                      onChange={(value) => {
                        setRouteId(value || null);
                        form.resetFields(["route_entry_point_id"]);
                      }}
                    >
                      {(routes.data || []).map((route) => (
                        <Option key={route._id} value={route._id}>
                          {route.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item label="Entry Point" name="route_entry_point_id" className="w-full">
                    <Select
                      showSearch
                      showArrow
                      allowClear
                      placeholder={routeId ? "Select entry point" : "Select a route first"}
                      className="w-full"
                      disabled={!routeId}
                    >
                      {(routesDetail.data || []).map((stop) => (
                        <Option key={stop._id} value={stop._id}>
                          {stop.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            )}

            <Title level={2}>Accommodation</Title>
            <Form.Item name="is_accommodation_required" valuePropName="checked">
              <Checkbox
                onChange={(e) => {
                  setAccommodationCheckbox(e.target.checked);
                  if (e.target.checked) {
                    setTransportationCheckbox(false);
                    form.setFieldsValue({ is_transportation_required: false });
                    form.resetFields(["transport_zone_id", "transport_destination_id", "route_id", "route_entry_point_id"]);
                    setSelectedZoneId(null);
                    setRouteId(null);
                  }
                }}
                checked={accommodationCheckbox}
              >
                Accommodation require?
              </Checkbox>
            </Form.Item>

            <Title level={2}>Day Care</Title>
            <Form.Item name="is_daycare_enabled" valuePropName="checked">
              <Checkbox
                onChange={(e) => setDaycareCheckbox(e.target.checked)}
                checked={daycareCheckbox}
              >
                Day care required?
              </Checkbox>
            </Form.Item>

            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-6">
                <Button
                  size="large"
                  type="primary"
                  htmlType="button"
                  className="text-white w-9"
                  onClick={() => setUploadAdmissionPopup(true)}
                >
                  Upload Document
                </Button>
                <Form.Item name="referral" label="Referral" className="w-30">
                  <Select
                    placeholder="Select Referral"
                    onChange={handleReferralChange}
                  >
                    <Option value="advertisement">Advertisement</Option>
                    <Option value="social">Social Networking Community</Option>
                    <Option value="newspaper">Newspaper</Option>
                    <Option value="friend">Friend</Option>
                  </Select>
                </Form.Item>
                {referral === "friend" && (
                  <div>
                    <Form.Item name="friendName" label="Name" className="w-30">
                      <Input
                        onChange={(e) =>
                          handleFriendInfoChange("name", e.target.value)
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      name="friendEmail"
                      label="Email"
                      className="w-30"
                      rules={[
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                    >
                      <Input
                        onChange={(e) =>
                          handleFriendInfoChange("email", e.target.value)
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      name="friendPhone"
                      label="Phone"
                      className="w-30"
                      rules={[
                        {
                          pattern: /^[0-9]{10}$/,
                          message: "Please enter a valid 10-digit phone number!",
                        },
                      ]}
                      getValueFromEvent={(e) => e.target.value.slice(0, 10)}
                    >
                      <Input
                        onChange={(e) =>
                          handleFriendInfoChange("phone", e.target.value)
                        }
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            </div>
            {parentFileList && parentFileList.length > 0 && (
              <div className="pt-4">
                <h5 className="pb-2 font-semibold text-base">
                  Attached Documents
                </h5>
                <ul>
                  {parentFileList.map((file, index) => (
                    <li key={file?.id || index} className="pb-2">
                      <a
                        href={file?.files?.original}
                        target="_blank"
                        rel="noreferrer"
                        className="text-base text-blue-500"
                      >
                        {file?.files?.name || "Untitled document"}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
            </Panel>
          </Collapse>
        </div>

        <div className="flex justify-center items-center py-5">
          <div className="inline-flex items-center space-x-2">
            {getResponse?.status !== "withdraw" && (
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                className="text-white"
                disabled={getResponse?.status === "approved"}
              >
                {getResponse?.status === "pending" ? "Update" : "Submit"}
              </Button>
            )}
          </div>
        </div>
        <Modal
          width={800}
          visible={uploadAdmissionPopup}
          onOk={() => setUploadAdmissionPopup(false)}
          onCancel={() => {
            setUploadAdmissionPopup(false);
          }}
          footer={[
            <Button
              key="done"
              type="primary"
              onClick={() => setUploadAdmissionPopup(false)}
            >
              Done
            </Button>,
          ]}
        >
          <div>
            <MultipleUpload
              onFileListChange={handleFileListChange}
              parentResponse={parentFileList}
            />
          </div>
        </Modal>
      </Form>
    </div>
  );
};

export default AddEditApplication;




